import { CamundaBaseService } from './base-service';
import { ENV_CONFIG } from '../env-config';
import { CamundaFormsService } from './forms-service';
import { getMimeTypeFromFilename, resolveRelevantTask } from './history-transform';
import { z } from 'zod';
import type { HistoryItem } from './types';
import type { CamundaFormValues } from './types';
import type {
  HistoricVariableInstance,
  HistoricProcessInstance,
  HistoricTaskInstance,
} from './history-types';
import {
  attachHistoricFileScope,
  isSerializedMultiFileArray,
  reconstituteMultiFileItems,
  type HistoricFileReference,
  type HistoricFileRevision,
} from './form-file-values';
import {
  HISTORY_ACCESS_REASONS,
  isValidHistoryAccessReasons,
  type HistoryAccessReason,
} from './history-access';

export interface HistoricTaskDetailAccessOptions {
  viewableTaskIds: Set<string>;
}

export type ParticipationHistoricFormResult =
  | {
    status: 'ok';
    variables: CamundaFormValues;
    unreadableVariableNames: string[];
  }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'error' };

export type ParticipationHistoricFileRevisionsResult =
  | { status: 'ok'; revisions: HistoricFileRevision[]; unreadableVariableNames: string[] }
  | { status: 'forbidden' }
  | { status: 'not_found' }
  | { status: 'error' };

export interface ParticipationListItemMetadata {
  processInstanceTitle: string | null;
  titulo: string | null;
  startUserName: string | null;
  assigneeDisplayName: string | null;
}

export type ParticipationHistoricTask = HistoricTaskInstance & {
  formKey: string | null;
};

export interface ParticipationStartForm {
  activityId: string;
  name: string | null;
  formKey: string;
}

type HistoricJsonValue =
  | string
  | number
  | boolean
  | null
  | HistoricJsonValue[]
  | { [key: string]: HistoricJsonValue };

const historicJsonValueSchema: z.ZodType<HistoricJsonValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(historicJsonValueSchema),
  z.record(z.string(), historicJsonValueSchema),
]));
const stringSchema = z.string();

const historicDateSchema = z.union([
  z.string(),
  z.number().int().min(-8_640_000_000_000_000).max(8_640_000_000_000_000)
    .transform((value) => new Date(value).toISOString()),
]);
const nullableHistoricStringSchema = z.string().nullish().transform((value) => value ?? null);
const nullableHistoricNumberSchema = z.number().nullish().transform((value) => value ?? null);
const nullableHistoricDateSchema = historicDateSchema.nullish().transform((value) => value ?? null);

const historicTaskSchema = z.object({
  id: z.string(),
  processDefinitionKey: nullableHistoricStringSchema,
  processDefinitionId: nullableHistoricStringSchema,
  processInstanceId: nullableHistoricStringSchema,
  executionId: nullableHistoricStringSchema,
  caseDefinitionKey: nullableHistoricStringSchema,
  caseDefinitionId: nullableHistoricStringSchema,
  caseInstanceId: nullableHistoricStringSchema,
  caseExecutionId: nullableHistoricStringSchema,
  activityInstanceId: z.string(),
  name: nullableHistoricStringSchema,
  description: nullableHistoricStringSchema,
  deleteReason: nullableHistoricStringSchema,
  owner: nullableHistoricStringSchema,
  assignee: nullableHistoricStringSchema,
  startTime: historicDateSchema,
  endTime: nullableHistoricDateSchema,
  duration: nullableHistoricNumberSchema,
  taskDefinitionKey: z.string(),
  priority: z.number(),
  due: nullableHistoricDateSchema,
  parentTaskId: nullableHistoricStringSchema,
  followUp: nullableHistoricDateSchema,
  tenantId: nullableHistoricStringSchema,
  removalTime: nullableHistoricDateSchema,
  rootProcessInstanceId: nullableHistoricStringSchema,
  taskState: z.string().nullable().optional(),
});

const historicProcessSchema = z.object({
  id: z.string(),
  businessKey: nullableHistoricStringSchema,
  processDefinitionId: z.string(),
  processDefinitionKey: z.string(),
  processDefinitionName: nullableHistoricStringSchema,
  processDefinitionVersion: z.number(),
  startTime: historicDateSchema,
  endTime: nullableHistoricDateSchema,
  removalTime: nullableHistoricDateSchema,
  durationInMillis: nullableHistoricNumberSchema,
  startUserId: nullableHistoricStringSchema,
  startActivityId: z.string(),
  deleteReason: nullableHistoricStringSchema,
  rootProcessInstanceId: nullableHistoricStringSchema,
  superProcessInstanceId: nullableHistoricStringSchema,
  superCaseInstanceId: nullableHistoricStringSchema,
  caseInstanceId: nullableHistoricStringSchema,
  tenantId: nullableHistoricStringSchema,
  state: z.string(),
});

const historicMetadataSchema = z.object({
  processInstanceTitle: z.string().nullable(),
  titulo: z.string().nullable(),
  startUserName: z.string().nullable(),
  assigneeDisplayName: z.string().nullable(),
});

const participationHistoricTaskSchema = historicTaskSchema.extend({
  formKey: nullableHistoricStringSchema,
});

const participationStartFormSchema = z.object({
  activityId: z.string(),
  name: z.string().nullable(),
  formKey: z.string(),
});

const stringIdArraySchema = z.array(z.string().min(1));
const userDisplayNamesSchema = z.record(z.string(), z.string());
const historyAccessReasonsSchema = z.array(z.enum(HISTORY_ACCESS_REASONS));

const participationProcessListResponseSchema = z.array(z.object({
  process: historicProcessSchema,
  tasks: z.array(historicTaskSchema),
  activeTasks: z.array(historicTaskSchema),
  metadata: historicMetadataSchema,
  accessReasons: historyAccessReasonsSchema,
}).superRefine((value, context) => {
  if (!isValidHistoryAccessReasons(value.accessReasons)) {
    context.addIssue({
      code: 'custom',
      path: ['accessReasons'],
      message: 'accessReasons must contain unique supported reasons',
    });
  }
}));

const participationProcessDetailResponseSchema = z.object({
  process: historicProcessSchema,
  tasks: z.array(participationHistoricTaskSchema),
  activeTasks: z.array(historicTaskSchema),
  startForm: participationStartFormSchema.nullish().transform((value) => value ?? null),
  participatedTaskIds: stringIdArraySchema,
  viewableTaskIds: stringIdArraySchema,
  workableTaskIds: stringIdArraySchema,
  userDisplayNames: userDisplayNamesSchema,
  isReviewer: z.boolean(),
  metadata: historicMetadataSchema,
  accessReasons: historyAccessReasonsSchema,
}).superRefine((value, context) => {
  if (!isValidHistoryAccessReasons(value.accessReasons, value.isReviewer)) {
    context.addIssue({
      code: 'custom',
      path: ['accessReasons'],
      message: 'accessReasons must contain unique supported reasons',
    });
  }
});

const participationProcessCountResponseSchema = z.object({
  count: z.number(),
});

const participationHistoricFormVariablesResponseSchema = z.object({
  variables: z.record(z.string(), historicJsonValueSchema),
  unreadableVariableNames: z.array(z.string()),
});

const historicFileRevisionSchema = z.object({
  _camundaType: z.literal('File'),
  historicDetailId: z.string().min(1),
  filename: z.string(),
  mimeType: z.string().nullable(),
  variableName: z.string(),
  revision: z.number().int(),
  engineRevision: z.number().int().nullable().optional(),
  occurredAt: z.string().nullable(),
  actor: z.string().nullable(),
});

const historicFileRevisionsResponseSchema = z.object({
  revisions: z.array(historicFileRevisionSchema),
  unreadableVariableNames: z.array(z.string()),
});

const historicTitleValueSchema = z
  .union([z.string(), z.number(), z.boolean()])
  .transform((value) => value.toString());

interface ProcessHistoryItemSource {
  process: HistoricProcessInstance;
  tasks: HistoricTaskInstance[];
  activeTasks: HistoricTaskInstance[];
  metadata: ParticipationListItemMetadata;
  accessReasons: HistoryAccessReason[];
}

export type {
  HistoricVariableInstance,
  HistoricProcessInstance,
  HistoricTaskInstance,
} from './history-types';

const PARTICIPATION_HISTORY_PAGE_SIZE = 50;

export class CamundaHistoryService extends CamundaBaseService {
  static async getHistoricVariableInstances(
    processInstanceId: string,
    variableName?: string
  ): Promise<HistoricVariableInstance[]> {
    try {
      const params = new URLSearchParams({
        processInstanceId,
        deserializeValues: 'false'
      });

      if (variableName) {
        params.append('variableName', variableName);
      }

      return await this.makeRequest<HistoricVariableInstance[]>(
        `/history/variable-instance?${params}`
      );
    } catch (error) {
      console.error('Error fetching historic variable instances:', error);
      return [];
    }
  }

  static async getHistoricVariable(
    processInstanceId: string,
    variableName: string
  ): Promise<HistoricVariableInstance | null> {
    try {
      const variables = await this.getHistoricVariableInstances(processInstanceId, variableName);
      return variables.length > 0 ? variables[0] : null;
    } catch (error) {
      console.error('Error fetching historic variable:', error);
      return null;
    }
  }

  static async getHistoricVariableBinary(variableInstanceId: string): Promise<Blob | null> {
    try {
      console.log(`Descargando binario para variable histórica ${variableInstanceId}`);
      const response = await this.makeRequest<Response>(
        `/history/variable-instance/${variableInstanceId}/data`,
        { rawResponse: true }
      );
      if (response.ok) {
        return response.blob();
      }
      console.error(`Error al descargar el fichero histórico: ${response.statusText}`);
      return null;
    } catch (error) {
      console.error('Error fetching historic variable binary:', error);
      return null;
    }
  }

  private static async resolveHistoricJsonVariableValue(
    variable: Pick<HistoricVariableInstance, 'id' | 'type' | 'value'>,
  ): Promise<CamundaFormValues[string]> {
    const type = variable.type?.toLowerCase();

    if (variable.value != null && variable.value !== '') {
      const serializedValue = stringSchema.safeParse(variable.value);
      if (serializedValue.success && (type === 'json' || type === 'object')) {
        try {
          const parsedValue = historicJsonValueSchema.safeParse(JSON.parse(serializedValue.data));
          return parsedValue.success ? parsedValue.data : variable.value;
        } catch {
          return variable.value;
        }
      }
      return variable.value;
    }

    if (type === 'json' || type === 'object') {
      const blob = await this.getHistoricVariableBinary(variable.id);
      if (!blob) {
        return null;
      }
      try {
        const parsedValue = historicJsonValueSchema.safeParse(JSON.parse(await blob.text()));
        return parsedValue.success ? parsedValue.data : null;
      } catch {
        return null;
      }
    }

    return variable.value;
  }

  static async getHistoricFileVariable(
    processInstanceId: string,
    variableName: string
  ): Promise<File | null> {
    try {
      const variable = await this.getHistoricVariable(processInstanceId, variableName);

      if (!variable) {
        console.log(`Variable '${variableName}' no encontrada en proceso ${processInstanceId}`);
        return null;
      }

      if (variable.type !== 'File') {
        console.log(`Variable '${variableName}' no es de tipo File, es ${variable.type}`);
        return null;
      }

      const { filename: resolvedFilename } = CamundaFormsService.resolveHistoricFileMetadata(variable);
      const filename = resolvedFilename || `${variableName}.pdf`;

      const fileContent = await this.getHistoricVariableBinary(variable.id);

      if (!fileContent) {
        console.log(`No se pudo descargar el contenido de la variable '${variableName}'`);
        return null;
      }

      const mimeType = getMimeTypeFromFilename(filename) || fileContent.type || 'application/octet-stream';

      const file = new File([fileContent], filename, { type: mimeType });
      console.log(`✅ Archivo '${filename}' cargado desde proceso histórico`, file);
      return file;
    } catch (error) {
      console.error('Error fetching historic file variable:', error);
      return null;
    }
  }

  static async getAvailableVariableNames(): Promise<string[]> {
    try {
      const variables = await this.makeRequest<HistoricVariableInstance[]>(
        '/history/variable-instance?deserializeValues=false&maxResults=1000'
      );

      const uniqueNames = [...new Set(variables.map(v => v.name))];
      return uniqueNames.sort();
    } catch (error) {
      console.error('Error fetching available variable names:', error);
      return [];
    }
  }

  static async getVariableExample(variableName: string): Promise<HistoricVariableInstance | null> {
    try {
      const variables = await this.makeRequest<HistoricVariableInstance[]>(
        `/history/variable-instance?variableName=${encodeURIComponent(variableName)}&deserializeValues=false&maxResults=1`
      );

      return variables.length > 0 ? variables[0] : null;
    } catch (error) {
      console.error('Error fetching variable example:', error);
      return null;
    }
  }









  private static getParticipationApiUrl(endpoint: string): string {
    const base = ENV_CONFIG.getBackendApiBaseUrl().replace(/\/$/, '');
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    return `${base}/participation${path}`;
  }

  private static participationBffError(action: string, status: number): Error {
    return new Error(`Participation BFF ${action} failed with HTTP ${status}`);
  }

  private static async fetchParticipationApi(
    endpoint: string,
    options: RequestInit = {},
  ): Promise<Response> {
    return this.fetchWithSessionRecovery(this.getParticipationApiUrl(endpoint), options);
  }

  private static async queryParticipationProcessCountFromBackend(params: {
    processDefinitionKey?: string;
    startedAfter?: string;
    startedBefore?: string;
  } = {}): Promise<number> {
    const searchParams = new URLSearchParams();
    if (params.processDefinitionKey) {
      searchParams.append('processDefinitionKey', params.processDefinitionKey);
    }
    if (params.startedAfter) {
      searchParams.append('startedAfter', params.startedAfter);
    }
    if (params.startedBefore) {
      searchParams.append('startedBefore', params.startedBefore);
    }
    const query = searchParams.toString();
    const endpoint = `/process-instances/count${query ? `?${query}` : ''}`;
    const response = await this.fetchParticipationApi(endpoint);
    if (!response.ok) {
      throw this.participationBffError('process-instances/count', response.status);
    }
    const parsedBody = participationProcessCountResponseSchema.safeParse(await response.json());
    if (!parsedBody.success) {
      throw new Error('Participation BFF process-instances/count returned invalid count');
    }
    return parsedBody.data.count;
  }

  private static async fetchParticipationProcessListFromBackend(params: {
    processDefinitionKey?: string;
    startedAfter?: string;
    startedBefore?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  }): Promise<Array<{
    process: HistoricProcessInstance;
    tasks: HistoricTaskInstance[];
    activeTasks: HistoricTaskInstance[];
    metadata: ParticipationListItemMetadata;
    accessReasons: HistoryAccessReason[];
  }>> {
    const searchParams = new URLSearchParams();
    if (params.processDefinitionKey) {
      searchParams.append('processDefinitionKey', params.processDefinitionKey);
    }
    if (params.startedAfter) {
      searchParams.append('startedAfter', params.startedAfter);
    }
    if (params.startedBefore) {
      searchParams.append('startedBefore', params.startedBefore);
    }
    searchParams.append('sortBy', params.sortBy || 'startTime');
    searchParams.append('sortOrder', params.sortOrder || 'desc');
    searchParams.append('firstResult', String(params.firstResult ?? 0));
    const cappedMaxResults = Math.min(
      params.maxResults ?? PARTICIPATION_HISTORY_PAGE_SIZE,
      PARTICIPATION_HISTORY_PAGE_SIZE,
    );
    searchParams.append('maxResults', String(cappedMaxResults));

    const response = await this.fetchParticipationApi(
      `/process-instances?${searchParams}`,
    );
    if (!response.ok) {
      throw this.participationBffError('process-instances', response.status);
    }

    const parsedBody = participationProcessListResponseSchema.safeParse(await response.json());
    if (!parsedBody.success) {
      throw new Error('Participation BFF process-instances returned invalid payload');
    }

    return parsedBody.data;
  }

  static async fetchParticipationProcessDetail(
    processInstanceId: string,
  ): Promise<
    | {
        process: HistoricProcessInstance;
        tasks: ParticipationHistoricTask[];
        activeTasks: HistoricTaskInstance[];
        startForm: ParticipationStartForm | null;
        participatedTaskIds: string[];
        viewableTaskIds: string[];
        workableTaskIds: string[];
        userDisplayNames: Record<string, string>;
        isReviewer: boolean;
        metadata: ParticipationListItemMetadata;
        accessReasons: HistoryAccessReason[];
      }
    | 'forbidden'
    | 'not_found'
    | 'error'
  > {
    try {
      const response = await this.fetchParticipationApi(
        `/process-instances/${encodeURIComponent(processInstanceId)}`,
      );
      if (response.status === 403 || response.status === 401) {
        return 'forbidden';
      }
      if (response.status === 404) {
        return 'not_found';
      }
      if (!response.ok) {
        console.error(
          `Participation BFF process-instances/${processInstanceId} failed with HTTP ${response.status}`,
        );
        return 'error';
      }

      const parsedBody = participationProcessDetailResponseSchema.safeParse(await response.json());
      if (!parsedBody.success) {
        console.error(
          `Participation BFF process-instances/${processInstanceId} returned invalid payload`,
        );
        return 'error';
      }

      return parsedBody.data;
    } catch (error) {
      console.error(
        `Participation BFF process-instances/${processInstanceId} request failed:`,
        error,
      );
      return 'error';
    }
  }

  static async fetchParticipationHistoricFormVariables(
    processInstanceId: string,
    taskId: string,
  ): Promise<ParticipationHistoricFormResult> {
    try {
      const response = await this.fetchParticipationApi(
        `/process-instances/${encodeURIComponent(processInstanceId)}`
        + `/tasks/${encodeURIComponent(taskId)}/form-variables`,
      );

      if (response.status === 401 || response.status === 403) {
        return { status: 'forbidden' };
      }
      if (response.status === 404) {
        return { status: 'not_found' };
      }
      if (!response.ok) {
        return { status: 'error' };
      }

      const parsedBody = participationHistoricFormVariablesResponseSchema.safeParse(
        await response.json(),
      );
      if (!parsedBody.success) {
        return { status: 'error' };
      }

      return {
        status: 'ok',
        variables: this.parseParticipationHistoricFormVariables(
          parsedBody.data.variables,
          processInstanceId,
          taskId,
        ),
        unreadableVariableNames: parsedBody.data.unreadableVariableNames,
      };
    } catch {
      return { status: 'error' };
    }
  }

  private static parseParticipationHistoricFormVariables(
    body: Record<string, HistoricJsonValue>,
    processInstanceId: string,
    taskId: string,
  ): CamundaFormValues {
    const formValues: CamundaFormValues = {};

    for (const [variableName, rawValue] of Object.entries(body)) {
      if (variableName === 'source') {
        continue;
      }

      formValues[variableName] = attachHistoricFileScope(
        CamundaHistoryService.reconstituteParticipationBffValue(rawValue),
        processInstanceId,
        taskId,
      );
    }

    return formValues;
  }

  private static reconstituteParticipationBffValue(
    value: CamundaFormValues[string],
  ): CamundaFormValues[string] {
    const fileConstructor = globalThis.File;
    const blobConstructor = globalThis.Blob;
    if (
      (fileConstructor && value instanceof fileConstructor)
      || (blobConstructor && value instanceof blobConstructor)
    ) {
      return value;
    }

    if (isSerializedMultiFileArray(value)) {
      return reconstituteMultiFileItems(value);
    }

    if (Array.isArray(value)) {
      return value.map((item) => CamundaHistoryService.reconstituteParticipationBffValue(item));
    }

    if (value instanceof Object) {
      return Object.fromEntries(
        Object.entries(value).map(([key, nested]) => [
          key,
          CamundaHistoryService.reconstituteParticipationBffValue(nested),
        ]),
      );
    }

    return value;
  }

  static async downloadParticipationHistoricFile(
    reference: HistoricFileReference,
  ): Promise<Blob> {
    const response = await this.fetchParticipationApi(
      `/process-instances/${encodeURIComponent(reference.processInstanceId)}`
      + `/tasks/${encodeURIComponent(reference.taskId)}`
      + `/form-variables/${encodeURIComponent(reference.historicDetailId)}/data`,
    );
    if (!response.ok) {
      throw this.participationBffError('historic file', response.status);
    }
    return response.blob();
  }

  static async fetchParticipationHistoricFileRevisions(
    processInstanceId: string,
    taskId: string,
  ): Promise<ParticipationHistoricFileRevisionsResult> {
    try {
      const response = await this.fetchParticipationApi(
        `/process-instances/${encodeURIComponent(processInstanceId)}`
        + `/tasks/${encodeURIComponent(taskId)}/form-file-revisions`,
      );
      if (response.status === 401 || response.status === 403) return { status: 'forbidden' };
      if (response.status === 404) return { status: 'not_found' };
      if (!response.ok) return { status: 'error' };
      const parsedBody = historicFileRevisionsResponseSchema.safeParse(await response.json());
      if (!parsedBody.success) {
        return { status: 'error' };
      }

      const revisions: HistoricFileRevision[] = parsedBody.data.revisions.map((revision) => ({
        ...revision,
        processInstanceId,
        taskId,
      }));
      return {
        status: 'ok',
        revisions,
        unreadableVariableNames: parsedBody.data.unreadableVariableNames,
      };
    } catch {
      return { status: 'error' };
    }
  }

  static canUserViewHistoricTaskDetail(
    taskId: string,
    options: HistoricTaskDetailAccessOptions,
  ): boolean {
    return options.viewableTaskIds.has(taskId);
  }

  static async getHistoricProcessInstances(params: {
    processDefinitionId?: string;
    processDefinitionKey?: string;
    businessKey?: string;
    startedBy?: string;
    processInstanceIds?: string;
    startedAfter?: string;
    startedBefore?: string;
    finished?: boolean;
    unfinished?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    maxResults?: number;
    firstResult?: number;
  }): Promise<HistoricProcessInstance[]> {
    try {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, value.toString());
        }
      });

      return await this.makeRequest<HistoricProcessInstance[]>(
        `/history/process-instance?${searchParams}`
      );
    } catch (error) {
      console.error('Error fetching historic process instances:', error);
      return [];
    }
  }

  static async getHistoricTaskInstance(taskId: string): Promise<HistoricTaskInstance | null> {
    try {
      const tasks = await this.getHistoricTaskInstances({ taskId, maxResults: 1 });
      return tasks[0] ?? null;
    } catch (error) {
      console.error('Error fetching historic task instance:', error);
      return null;
    }
  }

  static async getHistoricTaskInstances(params: {
    taskId?: string;
    processInstanceId?: string;
    processDefinitionId?: string;
    processDefinitionKey?: string;
    assignee?: string;
    taskInvolvedUser?: string;
    taskHadCandidateUser?: string;
    finished?: boolean;
    unfinished?: boolean;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    maxResults?: number;
    firstResult?: number;
  } = {}): Promise<HistoricTaskInstance[]> {
    try {
      const searchParams = new URLSearchParams();

      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          const queryKey = key === 'assignee' ? 'taskAssignee' : key;
          searchParams.append(queryKey, value.toString());
        }
      });

      return await this.makeRequest<HistoricTaskInstance[]>(
        `/history/task?${searchParams}`,
      );
    } catch (error) {
      console.error('Error fetching historic task instances:', error);
      return [];
    }
  }

  static async getBatchHistoricTaskInstances(
    processInstanceIds: string[],
    params: {
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      maxResults?: number;
    } = {}
  ): Promise<Map<string, HistoricTaskInstance[]>> {
    try {
      if (processInstanceIds.length === 0) {
        return new Map();
      }

      const tasksByProcess = new Map<string, HistoricTaskInstance[]>();
      processInstanceIds.forEach(id => {
        tasksByProcess.set(id, []);
      });

      const CHUNK_SIZE = 10;
      for (let i = 0; i < processInstanceIds.length; i += CHUNK_SIZE) {
        const chunk = processInstanceIds.slice(i, i + CHUNK_SIZE);
        await Promise.all(
          chunk.map(async (processInstanceId) => {
            const tasks = await this.getHistoricTaskInstances({
              processInstanceId,
              sortBy: params.sortBy || 'startTime',
              sortOrder: params.sortOrder || 'desc',
              maxResults: params.maxResults || 10000,
            });
            tasksByProcess.set(processInstanceId, tasks);
          }),
        );
      }

      return tasksByProcess;
    } catch (error) {
      console.error('Error fetching batch historic task instances:', error);
      return new Map();
    }
  }

  static async getBatchVariablesForProcesses(
    processInstanceIds: string[],
    variableNames?: string[],
  ): Promise<Map<string, Map<string, HistoricVariableInstance>>> {
    try {
      if (processInstanceIds.length === 0) {
        return new Map();
      }

      const params = new URLSearchParams({
        processInstanceIdIn: processInstanceIds.join(','),
        deserializeValues: 'false'
      });

      if (variableNames && variableNames.length > 0) {
        params.append('variableNameIn', variableNames.join(','));
      }

      const allVariables = await this.makeRequest<HistoricVariableInstance[]>(
        `/history/variable-instance?${params}`,
      );

      const variablesByProcess = new Map<string, Map<string, HistoricVariableInstance>>();

      processInstanceIds.forEach(id => {
        variablesByProcess.set(id, new Map());
      });

      allVariables.forEach(variable => {
        if (variable.processInstanceId && variablesByProcess.has(variable.processInstanceId)) {
          variablesByProcess.get(variable.processInstanceId)!.set(variable.name, variable);
        }
      });

      return variablesByProcess;
    } catch (error) {
      console.error('Error fetching batch variables for processes:', error);
      return new Map();
    }
  }

  static readonly PROCESS_TITLE_VARIABLE_NAME = 'titulo';

  static async getTitulosForProcessInstances(
    processInstanceIds: string[],
  ): Promise<Map<string, string | null>> {
    const unique = [
      ...new Set(
        processInstanceIds.filter((id) => id.trim() !== ''),
      ),
    ];
    const out = new Map<string, string | null>();
    if (unique.length === 0) {
      return out;
    }

    const CHUNK_SIZE = 75;
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += CHUNK_SIZE) {
      chunks.push(unique.slice(i, i + CHUNK_SIZE));
    }

    const partialMaps = await Promise.all(
      chunks.map((chunk) =>
        this.getBatchVariablesForProcesses(chunk, [this.PROCESS_TITLE_VARIABLE_NAME]),
      ),
    );

    for (const batchMap of partialMaps) {
      batchMap.forEach((varMap, procId) => {
        const hv = varMap.get(this.PROCESS_TITLE_VARIABLE_NAME);
        const titleValue = historicTitleValueSchema.safeParse(hv?.value);
        const text = titleValue.success && titleValue.data.trim() !== ''
          ? titleValue.data
          : null;
        out.set(procId, text);
      });
    }

    return out;
  }

  private static async createProcessHistoryItems(
    sources: ProcessHistoryItemSource[],
  ): Promise<HistoryItem[]> {
    if (sources.length === 0) {
      return [];
    }

    const relevantTaskByProcess = new Map<string, ReturnType<typeof resolveRelevantTask>>();
    for (const { process, tasks, activeTasks } of sources) {
      const isActive = !process.endTime;
      const tasksForMetadata =
        isActive && activeTasks.length > 0
          ? [...tasks, ...activeTasks]
          : tasks;
      const relevant = resolveRelevantTask(tasksForMetadata, isActive);
      relevantTaskByProcess.set(process.id, relevant);
    }

    return sources.map(({ process, tasks: processTasks, activeTasks, metadata, accessReasons }) => {
      const isActive = !process.endTime;
      const tasksForMetadata =
        isActive && activeTasks.length > 0
          ? [...processTasks, ...activeTasks]
          : processTasks;
      let assignee = '-';
      const { relevantTask, taskName } =
        relevantTaskByProcess.get(process.id) ?? resolveRelevantTask(tasksForMetadata, isActive);
      if (tasksForMetadata && tasksForMetadata.length > 0) {
        if (relevantTask?.assignee) {
          assignee =
            metadata.assigneeDisplayName ||
            relevantTask.assignee;
        } else if (!isActive) {
          assignee = '-';
        }
      }
      const resolvedState = process.state || (process.endTime ? 'COMPLETED' : 'ACTIVE');
      return {
        processInstanceId: process.id,
        processDefinitionName: process.processDefinitionName || process.processDefinitionKey,
        processDefinitionKey: process.processDefinitionKey,
        processInstanceTitle: metadata.processInstanceTitle,
        titulo: metadata.titulo,
        taskName: taskName,
        assignee: assignee,
        startTime: process.startTime,
        endTime: process.endTime || undefined,
        state: resolvedState,
        suspended: process.state === 'SUSPENDED',
        tenantId: process.tenantId,
        startUserName: metadata.startUserName || '-',
        accessReasons,
      };
    });
  }


  static async getHistoryTableData(params: {
    processDefinitionKey?: string;
    assignee?: string;
    startedAfter?: string;
    startedBefore?: string;
    maxResults?: number;
    includeProgress?: boolean;
    firstResult?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  } = {}): Promise<HistoryItem[]> {
    return this.getHistoryTableDataOptimized(params);
  }

  static async getHistoryTableDataCount(params: {
    processDefinitionKey?: string;
    assignee?: string;
    startedAfter?: string;
    startedBefore?: string;
  } = {}): Promise<number> {
    return this.getHistoryTableDataCountOptimized(params);
  }



  static async getHistoryTableDataOptimized(params: {
    processDefinitionKey?: string;
    startedAfter?: string;
    startedBefore?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    firstResult?: number;
    maxResults?: number;
  } = {}): Promise<HistoryItem[]> {
    const backendList = await this.fetchParticipationProcessListFromBackend({
      processDefinitionKey: params.processDefinitionKey,
      startedAfter: params.startedAfter,
      startedBefore: params.startedBefore,
      sortBy: params.sortBy || 'startTime',
      sortOrder: params.sortOrder || 'desc',
      firstResult: params.firstResult ?? 0,
      maxResults: params.maxResults ?? PARTICIPATION_HISTORY_PAGE_SIZE,
    });

    return this.createProcessHistoryItems(backendList);
  }

  static async getHistoryTableDataCountOptimized(params: {
    processDefinitionKey?: string;
    startedAfter?: string;
    startedBefore?: string;
  } = {}): Promise<number> {
    return this.queryParticipationProcessCountFromBackend({
      processDefinitionKey: params.processDefinitionKey,
      startedAfter: params.startedAfter,
      startedBefore: params.startedBefore,
    });
  }


}
